import { Injectable } from '@angular/core';
import { getDepartmentId, getTenantId } from 'src/app/services/store-snapshot.service';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { IdName } from 'src/app/shared/models/id-name';
import { Observable } from 'rxjs';
import { MainTableFilterModel } from 'src/app/shared/models/main-table-filter.model';
import { createFilterParams } from 'src/app/shared/functions/create-filter-params';
import { FormDataService } from 'src/app/shared/attachments/service/form-data.service';
import { MainTableFilteringHelperService } from 'src/app/shared/main-table-module/services/main-table-filtering-helper.service';
import {
  IRiskAssessmentRequest,
  IRiskAssessmentResponse,
  IRiskAssessmentLogVersion,
  IPerformInfoRequest,
  IRiskAssessmentPerformRequest,
  IApiResponseGroupRiskAssessments,
} from '../models/risk-assessment.model';

const COLUMN_TO_KEY_MAP: Record<string, string> = {
  nextExpiry: 'Expiration.IntervalExpiration.ExpiryDate',
  responsiblePositions: 'ResponsibleForPositions.Name',
  responsiblePositionGroups: 'ResponsibleForPositionGroups.Name',
  responsibleRoles: 'ResponsibleForRoles.Name',
};
const OR_GROUP_MAP: Record<string, string> = {
  responsiblePositions: 'responsible',
  responsiblePositionGroups: 'responsible',
  responsibleRoles: 'responsible',
};

@Injectable({
  providedIn: 'root',
})
export class RiskAssessmentApiService {
  headers = { 'content-type': 'application/json' };

  private get url(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/${getDepartmentId()}/RiskAssessment`;
  }

  private get tenantUrl(): string {
    return `${environment.apiUrl}/api/${getTenantId()}`;
  }

  private get searchUrl(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/RiskAssessment/Search`;
  }

  private get riskAssessmentsForIOUrl(): string {
    return `${this.url}/Interconnected`;
  }

  constructor(private http: HttpClient) {}

  getRiskAssessments(
    currentFilter?: MainTableFilterModel,
    menuId?: string
  ): Observable<IApiResponseGroupRiskAssessments> {
    // Disabled for now
    // const url = currentFilter?.searchTerm ? this.searchUrl : this.url;
    let params = createFilterParams(currentFilter, undefined, COLUMN_TO_KEY_MAP);
    if (menuId) {
      params = params.set('MenuId', menuId);
    }
    params = MainTableFilteringHelperService.appendFilteringParams(
      params,
      currentFilter,
      COLUMN_TO_KEY_MAP,
      OR_GROUP_MAP
    );

    return this.http.get<IApiResponseGroupRiskAssessments>(this.url, { params });
  }

  getRiskAssessmentsForIO(): Observable<IdName[]> {
    return this.http.get<IdName[]>(this.riskAssessmentsForIOUrl);
  }

  createRiskAssessment(riskAssessment: IRiskAssessmentRequest): Observable<IdName> {
    const departmentId =
      riskAssessment.departmentId !== '' ? riskAssessment.departmentId : getDepartmentId();
    const formData = FormDataService.mapToFormData(riskAssessment);
    const url = `${this.tenantUrl}/${departmentId}/RiskAssessment`;
    return this.http.post<IdName>(url, formData);
  }

  updateRiskAssessment(riskAssessment: IRiskAssessmentRequest): Observable<void> {
    const url = `${this.url}/${riskAssessment.id}`;
    const formData = FormDataService.mapToFormData(riskAssessment);
    return this.http.put<void>(url, formData);
  }

  getRiskAssessmentById(riskAssessmentId: string): Observable<IRiskAssessmentResponse> {
    const url = `${this.url}/${riskAssessmentId}`;
    return this.http.get<IRiskAssessmentResponse>(url);
  }

  deleteRiskAssessment(id: string) {
    const url = `${this.url}/${id}`;
    return this.http.delete<void>(url);
  }

  performRiskAssessment(
    performRequest: IPerformInfoRequest | IRiskAssessmentPerformRequest
  ): Observable<string> {
    const url = `${this.url}/${performRequest.id}/Perform`;
    const formData = FormDataService.mapToFormData(performRequest);
    return this.http.put<string>(url, formData);
  }

  getRiskAssessmentVersions(id: string): Observable<IRiskAssessmentLogVersion[]> {
    const url = `${this.url}/${id}/Versions`;
    return this.http.get<IRiskAssessmentLogVersion[]>(url);
  }

  getRiskAssessmentVersionById(id: string, versionId: string): Observable<IRiskAssessmentResponse> {
    const url = `${this.url}/${id}/Versions/${versionId}`;
    return this.http.get<IRiskAssessmentResponse>(url);
  }
}
